/**
 * FauxDS Tab
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Tab component for prototyping.
 * It is NOT the real @faire/slate Tab and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Tab, see: @faire/slate/components/tab
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import { Tag } from "@faire/slate/components/tag";
import React from "react";

export interface TabProps {
  /**
   * The label of the tab
   */
  label: React.ReactNode;
  /**
   * The value identifier for the tab
   */
  value: string;
  /**
   * **playground only**
   * Optional count badge displayed after the label (Slate uses `accessory` with Badge instead).
   */
  count?: number;
  /**
   * **playground only**
   * Small text badge after the label (e.g. “New” on Overview).
   */
  badge?: string;
  /**
   * Whether the tab is selected
   * @default false
   */
  selected?: boolean;
  /**
   * Callback when tab is selected
   */
  onSelect?: (value: string) => void;
  /**
   * **playground only**
   * Whether the tab is disabled (Slate Tab does not expose a direct `disabled` prop).
   * @default false
   */
  disabled?: boolean;
  /**
   * **playground only**
   * Optional className for additional styling (Slate Tab does not expose `className`).
   */
  className?: string;
}

/**
 * Tab - FauxDS (recreated from Slate)
 *
 * A single tab component. Use with TabGroup for tab navigation.
 * This is a simplified implementation for prototyping purposes.
 */
export function Tab({
  label,
  value,
  count,
  badge,
  selected = false,
  onSelect,
  disabled = false,
  className = "",
}: TabProps) {
  const handleClick = () => {
    if (!disabled) {
      onSelect?.(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && !disabled) {
      e.preventDefault();
      onSelect?.(value);
    }
  };

  return (
    <button
      role="tab"
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        "relative inline-flex items-center gap-1.5 px-1 pb-3 font-sans text-[14px] leading-[20px]",
        selected ? "font-medium" : "font-normal",
        "transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)] focus-visible:ring-offset-2 focus-visible:outline-hidden",
        selected
          ? "text-[var(--slate-color-text-primary)]"
          : "text-[var(--slate-color-text-subdued)] hover:text-[var(--slate-color-text-primary)]",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        className
      )}
    >
      <span className="whitespace-nowrap">{label}</span>
      {badge ? (
        <Tag label={badge} variant="neutral" className="shrink-0" />
      ) : null}
      {count != null && (
        <span className="ml-1.5 inline-flex min-w-[20px] items-center justify-center rounded-full bg-[#f0f0f0] px-1.5 py-0.5 text-[11px] leading-[14px] font-medium text-[#333]">
          {count}
        </span>
      )}
      {/* Active indicator line */}
      {selected && (
        <span
          className="absolute right-0 -bottom-px left-0 h-[2px] bg-[var(--slate-color-surface-primary-inverse)]"
          aria-hidden="true"
        />
      )}
    </button>
  );
}

export interface TabGroupProps {
  /**
   * The currently selected tab value
   */
  selectedTabValue?: string;
  /**
   * Callback when a tab is selected
   */
  onSelect?: (value: string) => void;
  /**
   * Tab components as children
   */
  children: React.ReactNode;
  /**
   * **playground only**
   * Optional className for additional styling (not a top-level prop on Slate TabGroup).
   */
  className?: string;
  /**
   * **playground only**
   * When true, hides the bottom border under the tab list (Slate TabGroup does not have this prop).
   */
  hideBorder?: boolean;
}

/**
 * TabGroup - FauxDS (recreated from Slate)
 *
 * Container for Tab components. Handles selection and keyboard navigation.
 * This is a simplified implementation for prototyping purposes.
 */
export function TabGroup({
  selectedTabValue: selectedValue,
  onSelect,
  children,
  className = "",
  hideBorder = false,
}: TabGroupProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const tabs = React.Children.toArray(children).filter(
      (child): child is React.ReactElement<TabProps> =>
        React.isValidElement(child) &&
        !(child as React.ReactElement<TabProps>).props.disabled
    );

    const currentIndex = tabs.findIndex(
      (tab) =>
        (tab as React.ReactElement<TabProps>).props.value === selectedValue
    );

    let newIndex = currentIndex;

    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      newIndex = (currentIndex + 1) % tabs.length;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (e.key === "Home") {
      e.preventDefault();
      newIndex = 0;
    } else if (e.key === "End") {
      e.preventDefault();
      newIndex = tabs.length - 1;
    }

    if (newIndex !== currentIndex && tabs[newIndex]) {
      onSelect?.((tabs[newIndex] as React.ReactElement<TabProps>).props.value);
    }
  };

  return (
    <div
      role="tablist"
      onKeyDown={handleKeyDown}
      className={cn(
        "flex gap-10",
        hideBorder ? "" : "border-b border-[var(--slate-color-border-muted)]",
        className
      )}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement<TabProps>(child)) {
          const tabChild = child as React.ReactElement<TabProps>;
          return React.cloneElement(child, {
            selected: tabChild.props.value === selectedValue,
            onSelect,
          });
        }
        return child;
      })}
    </div>
  );
}

export interface TabPanelProps {
  /**
   * The tab value this panel is associated with
   */
  tabValue: string;
  /**
   * The currently selected tab value
   */
  selectedTabValue: string;
  /**
   * Panel content
   */
  children: React.ReactNode;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * TabPanel - FauxDS (recreated from Slate)
 *
 * Content panel associated with a Tab.
 * This is a simplified implementation for prototyping purposes.
 */
export function TabPanel({
  tabValue,
  selectedTabValue: selectedValue,
  children,
  className = "",
}: TabPanelProps) {
  if (tabValue !== selectedValue) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      tabIndex={0}
      className={cn(
        "py-4 focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)] focus-visible:outline-hidden",
        className
      )}
    >
      {children}
    </div>
  );
}

export default Tab;
